<template>
  <el-dialog
    v-model="visible"
    :title="$t('about.feedback.title')"
    width="600px"
    :close-on-click-modal="false"
    class="feedback-dialog"
    @close="handleClose"
  >
    <el-form
      ref="feedbackFormRef"
      :model="feedbackForm"
      :rules="formRules"
      label-position="top"
      class="feedback-form"
    >
      <el-form-item :label="$t('about.feedback.type')" prop="type">
        <el-select
          v-model="feedbackForm.type"
          :placeholder="$t('about.feedback.types.placeholder')"
          size="large"
          style="width: 100%"
        >
          <el-option
            v-for="type in feedbackTypes"
            :key="type.value"
            :label="type.label"
            :value="type.value"
          >
            <div class="feedback-type-option">
              <el-icon :size="18">
                <component :is="type.icon" />
              </el-icon>
              <span>{{ type.label }}</span>
            </div>
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item :label="$t('about.feedback.subject')" prop="subject">
        <el-input
          v-model="feedbackForm.subject"
          :placeholder="$t('about.feedback.subjectPlaceholder')"
          size="large"
          maxlength="100"
          show-word-limit
        />
      </el-form-item>

      <el-form-item :label="$t('about.feedback.message')" prop="message">
        <el-input
          v-model="feedbackForm.message"
          type="textarea"
          :rows="6"
          :placeholder="$t('about.feedback.messagePlaceholder')"
          maxlength="1000"
          show-word-limit
        />
      </el-form-item>

      <el-form-item :label="$t('about.feedback.email')" prop="email">
        <el-input
          v-model="feedbackForm.email"
          :placeholder="$t('about.feedback.emailPlaceholder')"
          size="large"
        >
          <template #prefix>
            <el-icon><Message /></el-icon>
          </template>
        </el-input>
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="dialog-footer">
        <el-button size="large" @click="handleClose">
          {{ $t("common.cancel") }}
        </el-button>
        <el-button
          type="primary"
          size="large"
          :loading="submitting"
          @click="submitFeedback"
        >
          <el-icon v-if="!submitting"><Promotion /></el-icon>
          {{ $t("common.submit") }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from "vue";
import { ElMessage, type FormInstance, type FormRules } from "element-plus";
import { useI18n } from "vue-i18n";
import {
  ChatDotRound,
  Warning,
  MagicStick,
  Edit,
  Message,
  Promotion,
} from "@element-plus/icons-vue";
import { messagesApi } from "@/api/messages";
import { useAuthStore } from "@/stores/auth";

const { t } = useI18n();
const authStore = useAuthStore();

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const feedbackFormRef = ref<FormInstance>();
const submitting = ref(false);

const feedbackForm = reactive({
  type: "",
  subject: "",
  message: "",
  email: "",
});

const feedbackTypes = computed(() => [
  {
    value: "suggestion",
    label: t("about.feedback.types.suggestion"),
    icon: ChatDotRound,
  },
  {
    value: "bug",
    label: t("about.feedback.types.bug"),
    icon: Warning,
  },
  {
    value: "feature",
    label: t("about.feedback.types.feature"),
    icon: MagicStick,
  },
  {
    value: "general",
    label: t("about.feedback.types.general"),
    icon: Edit,
  },
]);

const formRules = computed<FormRules>(() => ({
  type: [
    {
      required: true,
      message: t("validation.required"),
      trigger: "change",
    },
  ],
  subject: [
    {
      required: true,
      message: t("validation.required"),
      trigger: "blur",
    },
    {
      min: 3,
      max: 100,
      message: t("validation.minLength", { min: 3 }),
      trigger: "blur",
    },
  ],
  message: [
    {
      required: true,
      message: t("validation.required"),
      trigger: "blur",
    },
    {
      min: 10,
      max: 1000,
      message: t("validation.minLength", { min: 10 }),
      trigger: "blur",
    },
  ],
  email: [
    {
      type: "email",
      message: t("validation.email"),
      trigger: "blur",
    },
  ],
}));

const submitFeedback = async () => {
  if (!feedbackFormRef.value) return;

  try {
    const valid = await feedbackFormRef.value.validate();
    if (!valid) return;

    submitting.value = true;

    // Send feedback to admin using dedicated feedback API
    const typeLabel = feedbackTypes.value.find(
      (type) => type.value === feedbackForm.type
    )?.label || feedbackForm.type;

    const feedbackData = {
      receiverId: 1, // This will be ignored by backend, but required for validation
      subject: `[${typeLabel.toUpperCase()}] ${feedbackForm.subject}`,
      content: `Type: ${typeLabel}\n${feedbackForm.email ? `Contact Email: ${feedbackForm.email}\n` : ""}Submitted by: ${authStore.user?.username || "Anonymous"}\n\n${feedbackForm.message}`,
    };

    await messagesApi.sendFeedback(feedbackData);

    ElMessage.success({
      message: t("about.feedback.successMessage"),
      duration: 3000,
    });

    handleClose();
  } catch (error: any) {
    console.error("Failed to submit feedback:", error);
    ElMessage.error(
      error.response?.data?.message ||
        t("about.feedback.errorMessage") ||
        "Failed to submit feedback. Please try again."
    );
  } finally {
    submitting.value = false;
  }
};

const handleClose = () => {
  visible.value = false;
  feedbackFormRef.value?.resetFields();
  Object.assign(feedbackForm, {
    type: "",
    subject: "",
    message: "",
    email: "",
  });
};

// Pre-fill email if user is logged in
watch(
  visible,
  (newVal) => {
    if (newVal && authStore.user?.email) {
      feedbackForm.email = authStore.user.email;
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.feedback-dialog :deep(.el-dialog__header) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  margin: 0;
  border-radius: 8px 8px 0 0;
}

.feedback-dialog :deep(.el-dialog__title) {
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
}

.feedback-dialog :deep(.el-dialog__headerbtn .el-dialog__close) {
  color: white;
  font-size: 20px;
}

.feedback-dialog :deep(.el-dialog__body) {
  padding: 30px;
}

.feedback-form {
  margin-top: 10px;
}

.feedback-form :deep(.el-form-item__label) {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.feedback-type-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.dialog-footer .el-button {
  min-width: 100px;
}

/* Form element styling */
.feedback-form :deep(.el-input__inner),
.feedback-form :deep(.el-textarea__inner) {
  border-radius: 8px;
}

.feedback-form :deep(.el-select .el-input__wrapper) {
  border-radius: 8px;
}

.feedback-form :deep(.el-textarea__inner) {
  font-family: inherit;
  line-height: 1.6;
}

/* Focus states */
.feedback-form :deep(.el-input__wrapper:focus-within),
.feedback-form :deep(.el-textarea__inner:focus) {
  box-shadow: 0 0 0 1px var(--el-color-primary) inset;
}
</style>
